import _ from 'lodash';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { TemplateFlexGridContainer } from '../../../components/Grid';
import { Heading } from '../../../components/Heading';
import { Icon } from '../../../components/Icon';
import { PackagingImage } from '../../../components/PackagingImage';
import { StatusIndicator } from '../../../components/StatusIndicator';
import { GTMBaseContentViewObject, PageTracker } from '../../../hooks/GTMUtils';
import { useBriefings } from '../../../hooks/queries';
import { useAppRoutes } from '../../../hooks/useAppRoutes';
import { useAuth } from '../../../hooks/useAuth';
import { useCountryMarketHelpers } from '../../../hooks/useCountryMarketHelpers';
import { useGTM } from '../../../hooks/useGTM';
import { getTaxonomyDisplayName } from '../../../lib/briefing';
import { formatDate } from '../../../lib/utils';
import { AttachedFile, Price, Step, TrackingSchema } from '../../../types';
import { getStepsByBranchByMarket } from '../../Briefing';
import { useBriefingState } from '../../Briefing/BriefingState';
import { displayOption } from '../../Briefing/BriefingUtils';
import { ReviewTableCell } from '../../Briefing/StepReview/StepReview';
import { FileDownload } from './FileDownload';

export const ProjectDetails: FC = () => {
  const { t, i18n } = useTranslation();
  const { briefingKey } = useParams();
  const { projectOverviewRoute } = useAppRoutes();
  const { updateDataLayer } = useGTM();
  const [{ displayUser }] = useAuth();
  const [userHasAccessToBriefing, setUserHasAccessToBriefing] = useState(false);
  const [{ modifyBriefingType }] = useBriefingState();
  const { findCountryMarketSlugsByStringsArray } = useCountryMarketHelpers();

  const { briefings } = useBriefings();

  const createOverviewTableElement = useCallback(
    (element: ReviewTableCell, index: number) => {
      if (element.heading) {
        return (
          <div className="w-full" key={index}>
            <TemplateFlexGridContainer.BriefingStepSection>
              <div className="mb-0 text-xl text-gray-600">
                {element.heading}
                <br />
                <span className="text-gray-700">
                  {element.content ? element.content : '-'}
                </span>
              </div>
            </TemplateFlexGridContainer.BriefingStepSection>
          </div>
        );
      } else {
        return (
          <div className="w-full" key={index}>
            <TemplateFlexGridContainer.BriefingStepSection>
              <div className="mb-0 text-xl text-gray-700">
                {element.content ? element.content : '-'}
              </div>
            </TemplateFlexGridContainer.BriefingStepSection>
          </div>
        );
      }
    },
    [],
  );

  const briefing = useMemo(() => briefings?.[briefingKey as string] ?? null, [briefingKey, briefings]);

  useEffect(() => {
    if (briefing) {
      setUserHasAccessToBriefing(
        findCountryMarketSlugsByStringsArray(
          displayUser?.countries_and_markets || {},
        ).some(
          (item) =>
            item.country === briefing.country &&
            item.market === briefing.marketSegment,
        ),
      );

      updateDataLayer({
        event: 'Formulation Check Status',
        schema: TrackingSchema.formulation,
        data: {
          status: briefing.status,
          creationDate: formatDate(new Date(briefing.createdAt), 'yyyy-mm-dd'),
          name: briefing.name,
        },
      });
    }
  }, [briefing, displayUser?.countries_and_markets, findCountryMarketSlugsByStringsArray, updateDataLayer]);

  if (!briefing || !userHasAccessToBriefing) {
    return <h1>No matching project found</h1>;
  }

  const steps = getStepsByBranchByMarket(
    briefing.marketSegment,
    modifyBriefingType,
  )[briefing.branch];

  const getFileList = (fileContainer: AttachedFile[]) => {
    return (
      <>
        <ul>
          {fileContainer.map((file: AttachedFile, index: number) => (
            <li key={index} className="flex mt-2">
              <div className="w-4 mr-1">
                <Icon name="File" />
              </div>
              <FileDownload file={file} />
            </li>
          ))}
        </ul>
        <p className="mt-1 text-sm">
          {t('components:projectDetails:fileDownloadHelp')}
        </p>
      </>
    );
  };

  const createDetailsField = (
    step: Step,
    value: ReviewTableCell[] | null,
    stepNo: number,
    isDoubleGrid = true,
  ) => (
    <div
      key={stepNo}
      className="flex w-full pb-1 mt-4 border-b border-gray-400"
    >
      <div className="w-full mr-1" data-testid={`step-${step}`}>
        <Heading
          level="h3"
          text={t([
            `topics:${step}:${briefing.branch}:name`,
            `topics:${step}:name`,
          ])}
        />
        <div className={`grid ${isDoubleGrid ? 'md:grid-cols-2' : ''}`}>
          {value
            ? value.map((element, index) =>
                createOverviewTableElement(element, index),
              )
            : '-'}
        </div>
      </div>
    </div>
  );

  const stepFields = () =>
    steps.map((step: Step, index: number) => {
      switch (step) {
        case 'application':
          return createDetailsField(
            step,
            briefing.applications.map((app, appIndex) => ({
              heading: t(`topics:application:levels:${appIndex}:selected`),
              content: getTaxonomyDisplayName(
                app,
                briefing.applicationsOther,
                t('labels:other'),
              ),
            })),
            index,
          );
        case 'target-consumer':
          return createDetailsField(
            step,
            [
              {
                heading: t('topics:target-consumer:age'),
                content: (briefing.targetConsumer.age ?? [])
                  .filter((age) => age)
                  .join(', '),
              },
              {
                heading: t('topics:target-consumer:gender'),
                content: briefing.targetConsumer.gender,
              },
            ],
            index,
          );
        case 'formulation-attributes':
          // @FIXME: remove duplication and find a more general approach
          if (briefing.marketSegment === 'FoodNutrition') {
            return createDetailsField(
              step,
              [
                {
                  heading: t(
                    'topics:formulation-attributes:productMarketingClaims',
                  ),
                  content: displayOption(
                    briefing.productMarketingClaims,
                    briefing.productMarketingClaimsOther,
                  ),
                },
                {
                  heading: t('topics:formulation-attributes:storage'),
                  content: displayOption(
                    briefing.storage,
                    briefing.storageOther,
                  ),
                },
              ],
              index,
            );
          }

          return createDetailsField(
            step,
            [
              {
                heading: t(
                  'topics:formulation-attributes:productMarketingClaims',
                ),
                content: displayOption(
                  briefing.productMarketingClaims,
                  briefing.productMarketingClaimsOther,
                ),
              },
              {
                heading: t('topics:formulation-attributes:formulationType'),
                content: displayOption(
                  briefing.formulationType,
                  briefing.formulationTypeOther,
                ),
              },
              {
                heading: t('topics:formulation-attributes:texture'),
                content: displayOption(briefing.texture, briefing.textureOther),
              },
            ],
            index,
          );
        case 'restrictions':
          return createDetailsField(
            step,
            briefing.restrictions &&
              (briefing.restrictions.restrictionsMust ||
                briefing.restrictions.restrictionsCan ||
                briefing.restrictions.requirementsMust ||
                briefing.restrictions.requirementsCan)
              ? [
                  {
                    heading: t('topics:restrictions:requirementsMust'),
                    content: displayOption(
                      briefing.restrictions.requirementsMust,
                      briefing.restrictions.requirementsMustOther,
                    ),
                  },
                  {
                    heading: t('topics:restrictions:requirementsCan'),
                    content: displayOption(
                      briefing.restrictions.requirementsCan,
                      briefing.restrictions.requirementsCanOther,
                    ),
                  },
                  {
                    heading: t('topics:restrictions:restrictionsMust'),
                    content: displayOption(
                      briefing.restrictions.restrictionsMust,
                      briefing.restrictions.restrictionsMustOther,
                    ),
                  },
                  {
                    heading: t('topics:restrictions:restrictionsCan'),
                    content: displayOption(
                      briefing.restrictions.restrictionsCan,
                      briefing.restrictions.restrictionsCanOther,
                    ),
                  },
                ]
              : null,
            index,
          );
        case 'price': {
          const displayPrice = (
            priceValue?: Price,
            currency?: string,
            weightUnit?: string,
          ) =>
            `${priceValue?.min || 0} - ${
              priceValue?.max || 'n.a.'
            } ${currency} / ${weightUnit}`;

          const prices = briefing.price?.prices;

          const priceKeys = _.keys(prices);
          const priceValues = _.values(prices);
          const priceEntries = _.entries(prices);

          return briefing.price?.min || briefing.price?.max
            ? createDetailsField(
                step,
                briefing.price?.min || briefing.price?.max
                  ? [
                      {
                        content: `${briefing.price.min || 0} - ${
                          briefing.price.max || 'n.a.'
                        } ${briefing.price.currency} / ${
                          briefing.price.baseUnit
                        }`,
                      },
                    ]
                  : null,
                index,
              )
            : createDetailsField(
                step,
                priceKeys.length
                  ? priceKeys.length === 1 && priceKeys[0] === 'base'
                    ? [
                        {
                          content: displayPrice(
                            priceValues[0],
                            briefing.price?.currency,
                            briefing.price?.baseUnit,
                          ),
                        },
                      ]
                    : priceEntries.map(([key, priceValue]) => ({
                        heading: t([
                          `topics:price:heading:${key}`,
                          `topics:price:heading:general`,
                        ]),
                        content: displayPrice(
                          priceValue,
                          briefing.price?.currency,
                          briefing.price?.baseUnit,
                        ),
                      }))
                  : null,
                index,
              );
        }
        case 'additional-info':
          return createDetailsField(
            step,
            briefing.additionalInfo || briefing.additionalInfoFiles
              ? [
                  {
                    heading: t('labels:notes'),
                    content: briefing.additionalInfo || null,
                  },
                  {
                    heading: t('labels:attachments'),
                    content:
                      briefing.additionalInfoFiles &&
                      briefing.additionalInfoFiles?.length > 0 ? (
                        <>{getFileList(briefing.additionalInfoFiles)}</>
                      ) : null,
                  },
                ]
              : null,
            index,
          );
        case 'problem':
          return createDetailsField(
            step,
            [{ content: briefing.problem }],
            index,
          );
        case 'packaging':
          if (briefing.packaging) {
            return createDetailsField(
              step,
              [
                {
                  content: displayOption(
                    briefing.packaging,
                    briefing.packagingOther,
                  ),
                },
              ],
              index,
            );
          }
          return null;
        case 'ingredients': {
          const { rawMaterial, ingredientsFiles, azelisRawMaterials } =
            briefing;
          return createDetailsField(
            step,
            rawMaterial || ingredientsFiles || azelisRawMaterials
              ? [
                  {
                    heading: t('topics:ingredients:azelisRawMaterials'),
                    content:
                      azelisRawMaterials && azelisRawMaterials.length > 0 ? (
                        <span className="whitespace-pre-line">
                          {azelisRawMaterials
                            .map(({ name }) => name)
                            .join('\n')}
                        </span>
                      ) : null,
                  },
                  {
                    heading: t('topics:ingredients:rawMaterials'),
                    content: rawMaterial || null,
                  },
                  {
                    heading: t('labels:attachments'),
                    content:
                      ingredientsFiles && ingredientsFiles?.length > 0 ? (
                        <>{getFileList(ingredientsFiles)}</>
                      ) : null,
                  },
                ]
              : null,
            index,
          );
        }
        case 'processing-capabilities':
          return createDetailsField(
            step,
            Object.entries(briefing.processingCapabilities)
              .filter((entry) => entry[1] !== null)
              .map((entry) => ({
                heading: t(`topics:processing-capabilities:${entry[0]}`),
                content: [
                  entry[1] !== false
                    ? entry[1]
                    : t('placeholders:notApplicable'),
                ],
              })),
            index,
          );
        default:
          return null;
      }
    });

  return (
    <PageTracker
      gtmTrackingData={{
        ...GTMBaseContentViewObject,
        data: {
          category: 'User Area',
          type: 'Detail',
          content: 'Formulation View',
        },
      }}
    >
      <div className="flex flex-col flex-wrap flex-grow space-y-7 xl:max-w-6xl">
        <div className="mx-5 mb-10 md:mx-8 lg:ml-7">
          <Link to={projectOverviewRoute}>
            <div className="flex mt-3 font-medium text-blue-200 hover:text-blue-100 hover:underline">
              <div className="w-4">
                <Icon name="ArrowLeft" inline={true} />
              </div>
              <div>
                {t('linksOrButtons:backToOverview').toLocaleUpperCase()}
              </div>
            </div>
          </Link>
          <div className="flex flex-wrap flex-grow xl:max-w-6xl">
            <div className="w-full">
              <div
                className="mt-7 md:mt-8 mb-7 md:mb-9"
                data-testid="page-header"
              >
                <Heading
                  level="h1"
                  margin={0}
                  text={briefing.name}
                />
                <div className="text-gray-600 mt-2">
                  {`${t('labels:createdAt')}: `}
                  {new Date(briefing.createdAt).toLocaleDateString(
                    i18n.language?.replace('_', '-'),
                  )}{' '}
                  {briefing.createdBy.first_name && briefing.createdBy.last_name
                    ? `- by ${briefing.createdBy.first_name} ${briefing.createdBy.last_name}`
                    : ''}
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap flex-grow xl:max-w-6xl">
            <div className="order-2 w-full lg:w-8/12 lg:order-1">
              <div className="mb-7">
                <Heading level="h3" text={t('labels:status')} />
                <StatusIndicator
                  status={briefing.status}
                  text={t(`labels:briefing:${briefing.status}`)}
                />
              </div>
              <div className="w-full pb-1 my-5">
                <Heading
                  level="h3"
                  text={t('components:projectDetails:referenceId')}
                />
                <div className="block mb-0 text-xl text-gray-700">
                  {briefing.displayId ?? briefingKey}
                </div>
              </div>
              <Heading
                level="h2"
                text={t('components:projectDetails:formulationSpecifics')}
              />
              <div className="flex w-full pb-1 mt-5 border-b border-gray-400">
                <div className="w-full mr-1">
                  <Heading
                    level="h3"
                    text={t(`components:projectDetails:projectType:heading`)}
                  />
                  <div
                    className="mb-0 text-xl text-gray-700"
                    data-testid="details-branch"
                  >
                    {t(
                      `components:projectDetails:projectType:${briefing.branch}`,
                    )}
                  </div>
                </div>
              </div>
              {stepFields()}
            </div>
            {briefing.packaging && briefing.packaging.name && (
              <div className="order-1 w-full lg:w-4/12 lg:order-2">
                <div className="mb-4 lg:mb-0">
                  <PackagingImage
                    isBig={true}
                    packagingName={briefing.packaging.name}
                    packagingValue={briefing.packaging.value}
                    flexJustify="justify-start"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </PageTracker>
  );
};
